import { FC, useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { InputSelect } from '@eneco/sparky';
import { DC_Products_Client_Old_Client_Models_FloorAreaM2 } from '@monorepo-types/dc';
import { usePlaceholderContent } from '@sitecore/common';
import { DecarbonizationLeadFlowBuildingAreaStepRendering } from '@sitecore/types/DecarbonizationLeadFlowBuildingAreaStep';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { DecarbonisationLeadFlowContext } from '../types';

interface FormFields {
  floorAreaM2: string;
}

const floorAreaFieldName = 'floorAreaM2';

export const BuildingAreaStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<DecarbonisationLeadFlowContext>();
  const sendToFlowMachine = useFlowActorRef().send;
  const flowContext = useFlowSelector(state => state.context);

  const data = usePlaceholderContent<{
    DecarbonizationLeadFlowBuildingAreaStep: DecarbonizationLeadFlowBuildingAreaStepRendering;
  }>();
  const { DecarbonizationLeadFlowBuildingAreaStep: { fields: textualData } = {} } = data;

  const businessTypeStepSchema = yup.object().shape({
    [floorAreaFieldName]: yup
      .string()
      .required(textualData?.choice?.buildingAreaInputSelectField?.value?.requiredMessage),
  });

  const {
    handleSubmit,
    getValues,
    register,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    defaultValues: {
      floorAreaM2: flowContext.floorAreaM2 || undefined,
    },
    mode: 'onSubmit',
    resolver: typedYupResolver<FormFields>(businessTypeStepSchema),
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formValues = getValues();

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          ...flowContext,
          floorAreaM2: formValues[floorAreaFieldName] as DC_Products_Client_Old_Client_Models_FloorAreaM2,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Layout.Content variant="B" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && <RichText html={textualData?.content?.text?.value} />}
      </Header>

      <InputSelect
        {...register(floorAreaFieldName)}
        label={textualData?.choice?.buildingAreaInputSelectField?.value?.label ?? ''}
        error={errors?.floorAreaM2?.message}
        options={textualData?.choice?.buildingAreaInputSelectField?.value?.options ?? []}
        placeholder={textualData?.choice?.buildingAreaInputSelectField?.value?.placeholder ?? ''}
        isOptional={false}
      />

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
